/**
 * Financial Precision Configuration
 * Centralized settings for decimal precision across all financial inputs
 *
 * CATEGORIES:
 * - MONEY_AMOUNTS: All currency values (revenue, costs, allocations, invoices)
 * - EXCHANGE_RATES: Currency conversion rates
 * - PERCENTAGES: Percentage displays
 *
 * Each category includes:
 * - precision: Maximum decimal places allowed
 * - minStep: Smallest increment for input stepping
 * - tolerance: Balance detection threshold (when is variance "close enough to zero")
 * - displayMinDecimals: Minimum decimals to always show
 * - displayMaxDecimals: Maximum decimals to show
 */

// ========== MONEY AMOUNTS (General Currency Values) ==========
/**
 * Use for: All monetary amounts in any currency
 * Examples: Project revenue, vendor costs, invoice amounts, allocations, sales orders, budgets, expenses
 *
 * Industry Standard: 2-4 decimals (Microsoft Dynamics: max 4, ISO 4217: 0-3)
 * Our Implementation: Store up to 12 decimals for precision, display up to 4 for standard compliance
 */
export const MONEY_AMOUNTS = {
  precision: 12,              // Maximum input/storage precision (supports ultra-precise calculations)
  minStep: 0.01,              // Step increment for up/down arrows (1 cent - practical for UI)
  tolerance: 0.01,            // Balance detection threshold: 1 cent (practical, industry standard)
  displayMinDecimals: 2,      // Always show at least 2 decimals (e.g., 1,000.50)
  displayMaxDecimals: 12,     // Show up to 12 decimals when entered (full precision visible to user)
};

// ========== EXCHANGE RATES (Currency Conversion) ==========
/**
 * Use for: All exchange/conversion rates
 * Examples: Custom vendor rates, currency API rates, historical rates
 *
 * Industry Standard: 4-7 decimals (Forex: 4-6, OANDA: 6-7 recommended)
 * Our Implementation: Store up to 12 decimals for precision, display up to 6 for standard compliance
 */
export const EXCHANGE_RATES = {
  precision: 12,              // Maximum input/storage precision (supports exotic pairs)
  minStep: 0.01,              // Step increment for up/down arrows (0.01 - standard increment)
  tolerance: 0.0001,          // Balance detection threshold: 4 decimals (practical)
  minValue: 0.0001,           // Minimum allowed rate (4-decimal precision)
  displayMinDecimals: 2,      // Show at least 2 decimals
  displayMaxDecimals: 12,     // Show up to 12 decimals when entered (full precision visible to user)
};

// ========== PERCENTAGES (%, Ratios, Margins) ==========
/**
 * Use for: All percentage displays
 * Examples: Allocation percentages, profit margins, completion rates, probability
 */
export const PERCENTAGES = {
  precision: 2,               // Maximum decimal places (e.g., 45.75%)
  minStep: 0.01,              // Smallest increment (0.01%)
  tolerance: 0.01,            // Balance threshold for percentages (1% tolerance)
  displayMinDecimals: 2,      // Always 2 decimals (e.g., 45.50%)
  displayMaxDecimals: 2,      // Never more than 2
};

// ========== DISPLAY ONLY (Read-Only Views) ==========
/**
 * Use for: Read-only/view-mode displays (not editable inputs)
 * Examples: Invoice allocation modal view mode, summary tables, reports
 *
 * Same precision for calculations but limited display for cleaner UX
 */
export const DISPLAY_ONLY = {
  precision: 12,              // Calculate with 12 decimals (same as MONEY_AMOUNTS)
  minStep: 0.01,              // Not used for display-only
  tolerance: 0.000000000001,  // Same tolerance as MONEY_AMOUNTS
  displayMinDecimals: 2,      // Always show 2 decimals
  displayMaxDecimals: 2,      // Limit to 2 decimals for cleaner display
};

// ========== BACKWARD COMPATIBILITY ==========
/**
 * Legacy VARIANCE_TOLERANCE export
 * Use MONEY_AMOUNTS.tolerance instead in new code
 */
export const VARIANCE_TOLERANCE = MONEY_AMOUNTS.tolerance;

// ========== HELPER FUNCTIONS ==========

/**
 * Get step value for a category
 * @param {object} category - Precision category
 * @returns {number} Step value for InputNumber component
 */
export const getStepValue = (category) => {
  return category.minStep;
};

/**
 * Round value to category precision.
 *
 * Scale-aware: JS doubles carry ~15 significant digits, so a fixed
 * `Math.round(v * 1e12) / 1e12` is a no-op on the LSB for values with
 * 4+ integer digits. Example: `Math.round((95405.20 - 84434.25) * 1e12) / 1e12`
 * stays at `10970.949999999999` (artifact-laden) because the multiplier
 * overshoots the precision budget. We cap effective precision at
 * `15 - integerDigits` so the rounding actually normalises subtraction
 * artifacts at money magnitudes.
 *
 * @param {number} value - Value to round
 * @param {object} category - Precision category
 * @returns {number} Rounded value
 *
 * @example
 * roundToPrecision(1234.123456789012, MONEY_AMOUNTS)  // 1234.12345678901
 * roundToPrecision(95405.20 - 84434.25, MONEY_AMOUNTS) // 10970.95
 * roundToPrecision(45.678, PERCENTAGES)                // 45.68
 */
export const roundToPrecision = (value, category) => {
  const requestedPrecision = category.precision || 12;
  const absValue = Math.abs(value);
  const integerDigits = absValue >= 1 ? Math.floor(Math.log10(absValue)) + 1 : 1;
  const safePrecision = Math.max(0, Math.min(requestedPrecision, 15 - integerDigits));
  const multiplier = Math.pow(10, safePrecision);
  return Math.round(value * multiplier) / multiplier;
};

/**
 * Check if variance is within tolerance for a category
 *
 * @param {number} variance - The variance value
 * @param {object} category - The precision category (default: MONEY_AMOUNTS)
 * @returns {boolean} True if variance is within tolerance (balanced)
 *
 * @example
 * isBalanced(0.0000000001, MONEY_AMOUNTS)  // true
 * isBalanced(0.5, PERCENTAGES)             // false
 */
export const isBalanced = (variance, category = MONEY_AMOUNTS) => {
  return Math.abs(variance) <= category.tolerance;
};

/**
 * Get display decimals range for category
 * @param {object} category - Precision category
 * @returns {object} Object with min and max decimal places
 */
export const getDisplayDecimals = (category) => {
  return {
    min: category.displayMinDecimals,
    max: category.displayMaxDecimals
  };
};
